import React, { useState, useEffect } from 'react'
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { getCourseOfferingApi } from '../../../Api/CourseOffering';
import { getCourseApi } from '../../../Api/Course';
import { getCourseTypeApi } from '../../../Api/CourseType';
import { addBatchApi, updateBatchApi, getBatchByIdApi } from '../../../Api/Batch';
import BatchStudents from './BatchForm Component/BatchStudents';
import BatchEmployee from './BatchForm Component/BatchEmployee';
import { addBatchStudentApi } from '../../../Api/BatchStudent';

interface Course {
    id: string;
    name: string;
}
type CourseTypeInterface = {
    id: string;
    name: string;
    fees: number;
};
interface Props {
    url: string;
}
interface Props {
    batchData: any;
    onClose: () => void;
    onSuccess?:() => void;
}
export function BatchForm({ url, batchData, onClose, onSuccess}: Props) {
    const navigate = useNavigate();
    const location = useLocation();
    const [Name, setName] = useState("");
    const [Course, setCourse] = useState<string>("");
    const [CourseType, setCourseType] = useState("");
    const [StartTime, setStartTime] = useState("");
    const [EndTime, setEndTime] = useState("");
    const [Status, setStatus] = useState<string>("");
    const [CourseOfferings, setCourseOfferings] = useState([]);
    const [AllCourses, setAllCourses] = useState([]); // Store the original list of courses
    const [Courses, setCourses] = useState([]); // Filtered courses
    const [CourseTypes, setCourseTypes] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const id = batchData?.id;


    useEffect(() => {
        if (batchData) {
            setName(batchData.name);
            setCourse(batchData.courseId);
            setCourseType(batchData.courseTypeId);
            setStartTime(batchData.startTime);
            setEndTime(batchData.endTime);
            setStatus(batchData.status || "");
        }

    }, [batchData]);

    // Extract id from batchData if available


    useEffect(() => {
        const fetchBatchData = async () => {
            if (id) {

                try {
                    const batch = await getBatchByIdApi(url, id);
                    setIsEdit(true);
                    setName(batch.name);

                    // Find the course ID based on the course name
                    const courseId = Courses.find((course: any) => course.name === batch.course)?.id;
                    setCourse(courseId || ""); // Set the course ID or an empty string if not found

                    // Find the course type ID based on the course type name
                    const courseTypeId = CourseTypes.find((type: any) => type.name === batch.courseType)?.id;
                    setCourseType(courseTypeId || ""); // Set the course type ID or an empty string if not found

                    setStartTime(batch.startTime);
                    setEndTime(batch.endTime);
                    setStatus(batch.status || "");
                } catch (error) {
                    toast.error("Failed to fetch batch details.");
                }
            }
        };

        fetchBatchData();
    }, [id, url, Courses, CourseTypes]);

    const handleApiCallClick = async () => {
        try {
            const offerings = await getCourseOfferingApi(url);
            const courseResponse = await getCourseApi(url);
            const courseTypeResponse = await getCourseTypeApi(url);

            // Extract courseIds and courseTypeIds from offerings
            const courseIds = offerings.map((offering: any) => offering.courseId);
            const courseTypeIds = offerings.map((offering: any) => offering.courseTypeId);

            // Filter courses and course types based on offerings
            const filteredCourses = courseResponse.filter((course: any) => courseIds.includes(course.id));
            const filteredCourseTypes = courseTypeResponse.filter((courseType: any) => courseTypeIds.includes(courseType.id));

            setCourseOfferings(offerings);
            setAllCourses(filteredCourses); // Store the original list of courses
            setCourses(filteredCourses); // Initially set filtered courses
            setCourseTypes(filteredCourseTypes);
        } catch (error) {
            toast.error("Failed to fetch data. Some problem occurred. Please try again.");
        }
    };

    useEffect(() => {
        handleApiCallClick();
    }, []);

    useEffect(() => {
        if (CourseType) {
            // Filter courses based on the selected CourseType
            const filteredCourses = CourseOfferings
                .filter((offering: any) => offering.courseTypeId === CourseType)
                .map((offering: any) => offering.courseId);

            const updatedCourses = AllCourses.filter((course: any) => filteredCourses.includes(course.id));
            setCourses(updatedCourses);
        } else {
            // Reset to all courses if no CourseType is selected
            setCourses(AllCourses);
        }
    }, [CourseType]);

    function handleSubmitClick(event: React.FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        if (!Name || !Course || !CourseType || !StartTime || !EndTime || !Status) {
            toast.error("Please fill in all the fields.");
            return;
        }

        const coursename = Courses.find((course: Course) => course.id === Course)?.name;
        const courseTypeName = CourseTypes.find((type: CourseTypeInterface) => type.id === CourseType)?.name;
        const Fees = CourseTypes.find((type: CourseTypeInterface) => type.id === CourseType)?.fees;


        if (isEdit) {
            // Update existing batch
            updateBatchApi(url, {
                Id: id, // Pass the batch ID for updating
                Name,
                CourseType: courseTypeName,
                Course: coursename,
                StartTime,
                EndTime,
                Status,
            })
                .then((response) => {
                    toast.success("Batch updated successfully!");
                   onClose();
                    if(onSuccess) onSuccess();
                   
                })
                .catch((error) => {
                    console.error("Error updating batch:", error);
                    toast.error("Failed to update batch. Please try again.");
                });
        } else {
            // Create new batch
            addBatchApi(
                url,
                Name,
                coursename,
                courseTypeName,
                StartTime,
                EndTime,
                Fees,
                Status
            )
                .then(async (response) => {
                    toast.success("Batch created successfully!");
                    const createdBatchId =response?.id;

                    if (batchData?.StudentId && batchData?.RegId && createdBatchId) {
                        try {
                            await addBatchStudentApi(
                                url,
                                createdBatchId,
                                batchData.StudentId,
                                batchData.RegId
                            );
                            toast.success("Student assigned to batch.");
                            navigate(-1);
                        } catch (assignError) {
                            console.error("Error adding student to batch:", assignError);
                            toast.error("Batch created, but assigning student failed.");
                        }
                    }

                    onClose();
                })
                .catch((error) => {
                    console.error("Error creating batch:", error);
                    toast.error("Failed to create batch. Please try again.");
                });
        }

        // Reset form fields
        setName("");
        setCourse("");
        setCourseType("");
        setStartTime("");
        setEndTime("");
        setStatus("");
    }
    const [segmentActiveTab, setsegmentActiveTab] = useState<string>("segment-1");

    return (
        <div className="">

            <h2 className="text-3xl font-bold text-blue-600 mb-5">{isEdit ? "Edit Batch" : "Create Batch"}</h2>

            <form onSubmit={handleSubmitClick} className="mt-8 space-y-4">
                <div className="flex flex-col gap-4">
                    <div>
                        <label
                            htmlFor="course"
                            className="block text-sm font-medium text-black dark:text-gray-200 mb-2"
                        >
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="Name"
                            type="text"
                            required
                            placeholder="Name"
                            value={Name}
                            onChange={(event) => setName(event.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex flex-col space-y-2">
                        <label
                            htmlFor="courseType"
                            className="block text-sm font-medium text-black dark:text-gray-200"
                        >
                            Course Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="courseType"
                            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                            value={CourseType}
                            onChange={(e) => setCourseType(e.target.value)}
                        >
                            <option value="" disabled>
                                Select Course Type
                            </option>
                            {CourseTypes.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex mb-2 flex-col space-y-2">
                        <label
                            htmlFor="course"
                            className="block text-sm font-medium text-black dark:text-gray-200"
                        >
                            Course <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="course"
                            className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                            value={Course}
                            onChange={(e) => setCourse(e.target.value)}
                        >
                            <option value="" disabled>
                                Select Course
                            </option>
                            {Courses.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col space-y-2 w-full">
                        <label className={`block text-sm font-medium text-black dark:text-gray-200 mb-2 `}>
                            Status <span className="text-red-500">*</span>
                        </label>
                        <select
                            required
                            name="status"
                            value={Status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="" disabled>Select Status</option>
                            <option value="New">New</option>
                            <option value="Active">Active</option>
                            <option value="Stopped">Stopped</option>
                            <option value="On Hold">On Hold</option>
                        </select>
                    </div>

                    <div className="flex justify-between">
                        <div className="w-1/2 pr-2">
                            <label htmlFor="customStartTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                Start Time <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="time"
                                id="customStartTime"
                                value={StartTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                required
                            />
                        </div>
                        <div className="w-1/2 pl-2">
                            <label htmlFor="customEndTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                End Time <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="time"
                                id="customEndTime"
                                value={EndTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-start space-x-4">
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {isEdit ? "Update" : "Save and close"}
                    </button>
                    <button
                        type="button"
                        onClick={() => onClose()}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                        Cancel
                    </button>
                </div>
            </form>

            {isEdit && (
                <div className="mt-8">
                    <div className="flex overflow-x-auto whitespace-nowrap border-b border-gray-300 dark:border-gray-500">
                        <button
                            className={`inline-flex items-center h-12 px-4 py-2 text-center text-gray-700 border border-b-0 border-gray-300 dark:border-gray-500 rounded-t-md focus:outline-none whitespace-nowrap transition-all 
                         ${segmentActiveTab === "segment-1" ? "bg-blue-500 dark:bg-blue-700 text-white border-gray-400 dark:border-gray-300" : "bg-transparent text-gray-500 hover:border-gray-400 dark:hover:border-gray-300"}`}
                            onClick={() => setsegmentActiveTab("segment-1")}
                        >
                            <span className="mx-1 text-sm sm:text-base">Employees</span>
                        </button>

                        <button
                            className={`inline-flex items-center h-12 px-4 py-2 text-center text-gray-700 border border-b-0 border-gray-300 dark:border-gray-500 rounded-t-md  focus:outline-none whitespace-nowrap transition-all 
                         ${segmentActiveTab === "segment-2" ? "bg-blue-500 dark:bg-blue-700 text-white border-gray-400 dark:border-gray-300" : "bg-transparent text-gray-500 hover:border-gray-400 dark:hover:border-gray-300"}`}
                            onClick={() => setsegmentActiveTab("segment-2")}
                        >
                            <span className="mx-1 text-sm sm:text-base">Students</span>
                        </button>
                    </div>
                    {
                        segmentActiveTab === "segment-1" ? (
                            <BatchEmployee url={url} batchId={id} />
                        ) : (
                            <BatchStudents url={url} batchId={id} />
                        )
                    }
                </div>
            )}

        </div>
    );
}

export default BatchForm